import type {
  MetricsConfig,
  EventProperties,
  ErrorContext,
  RevenueData,
  MetricData,
  MetricType,
  PageViewData,
  AiUsageData,
  AiCostRow,
  AiCostMetricRecord,
} from './types.js';

import * as SentryProvider from './providers/sentry-react.js';
import * as PosthogProvider from './providers/posthog.js';
import * as SupabaseProvider from './providers/supabase.js';
import * as GaProvider from './providers/ga.js';
import { getGaMeasurementId as getGaIdFromConfig } from './ga-config.js';
import {
  AI_TOKEN_COST_METRIC,
  aggregateAiCostRows,
} from './ai-usage.js';

let config: MetricsConfig | null = null;

// Store user data for page views
let userData: { userId?: string; plan?: string } = {};

/**
 * Set user data for page view tracking
 * Call this when user logs in or updates their profile
 */
export function setPostHogUserData(data: { userId?: string; plan?: string }) {
  userData = { ...userData, ...data };
  
  // Also store in sessionStorage for client-side access
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem('posthog_user_data', JSON.stringify(userData));
    } catch {
      // Ignore storage errors
    }
  }
}

/**
 * Get stored user data for page view tracking
 */
export function getPostHogUserData(): { userId?: string; plan?: string } {
  return userData;
}

/**
 * Initialize metrics collection for your app
 * Call this once at app startup (e.g., in _app.tsx or App.tsx)
 */
export function initMetrics(userConfig: MetricsConfig) {
  config = {
    environment: process.env.NODE_ENV as any || 'development',
    enableSentry: true,
    enablePosthog: true,
    enableCustomMetrics: true,
    ...userConfig,
  };

  // Initialize Sentry for error tracking
  if (config.enableSentry && config.sentryDsn) {
    SentryProvider.initSentry(
      config.sentryDsn,
      config.appName,
      config.environment!
    );
  }

  // Initialize PostHog for product analytics
  if (config.enablePosthog && config.posthogKey) {
    PosthogProvider.initPosthog(
      config.posthogKey,
      config.posthogHost || 'https://app.posthog.com',
      config.appName
    );
  }

  // Initialize Supabase for custom metrics
  if (config.enableCustomMetrics && config.supabaseUrl && config.supabaseAnonKey) {
    SupabaseProvider.initSupabase(
      config.supabaseUrl,
      config.supabaseAnonKey,
      config.appName
    );
  }

  // Initialize GA4 (BuildLogic property from env) when measurement ID is set
  const gaId = config.gaMeasurementId ?? getGaIdFromConfig();
  if (gaId && config.enableGa !== false) {
    GaProvider.initGa(gaId);
  }

  console.log(`[Metrics] Initialized for ${config.appName} (${config.environment})`);
}

/**
 * Track a custom event (e.g., button click, feature usage)
 */
export function trackEvent(eventName: string, properties?: EventProperties) {
  if (config?.enablePosthog) {
    PosthogProvider.trackEvent(eventName, properties);
  }
  GaProvider.trackEvent(eventName, properties);
}

export function trackCareerProofView(path: string) {
  trackEvent('career_proof_viewed', { path });
}

export function trackCareerProofArtifactOpened(
  artifactId: string,
  lane: string,
  href: string,
) {
  trackEvent('career_proof_artifact_opened', { artifact_id: artifactId, lane, href });
}

export function trackCareerLeadSubmitted(source: string) {
  trackEvent('career_lead_submitted', { source });
}

export function trackCareerLeadDelivered(source: string, operation: string) {
  trackEvent('career_lead_delivered', { source, operation });
}

export function trackCareerLeadFailed(source: string, status: number) {
  trackEvent('career_lead_failed', { source, status });
}

export { COMMERCE_EVENTS } from './commerce.js';
export type { CommerceEventName } from './commerce.js';

export function trackCommerceEvent(
  eventName: import('./commerce.js').CommerceEventName,
  properties?: EventProperties,
) {
  trackEvent(eventName, properties);
  if (eventName === 'commerce.payment.succeeded' && typeof properties?.amount === 'number') {
    trackRevenue({
      amount: properties.amount,
      currency: typeof properties.currency === 'string' ? properties.currency : 'usd',
      plan: typeof properties.sku === 'string' ? properties.sku : undefined,
      userId: typeof properties.userId === 'string' ? properties.userId : undefined,
      metadata: properties,
    });
  }
}

/**
 * Track an error for debugging and alerting
 */
export function trackError(error: Error, context?: ErrorContext) {
  if (!config) return;

  // Log to console in development
  if (config.environment === 'development') {
    console.error('[Metrics] Error:', error, context);
  }

  // Send to Sentry
  if (config.enableSentry) {
    SentryProvider.captureError(error, context);
  }

  // Record in custom metrics
  if (config.enableCustomMetrics) {
    SupabaseProvider.recordMetric('error', 1, {
      message: error.message,
      stack: error.stack,
      ...context,
    });
  }
}

/**
 * Track revenue events (subscriptions, purchases)
 */
export function trackRevenue(data: RevenueData) {
  if (!config) return;

  // Track in PostHog
  if (config.enablePosthog) {
    PosthogProvider.trackRevenue(data);
  }

  // Record in custom metrics
  if (config.enableCustomMetrics) {
    SupabaseProvider.recordMetric('revenue', data.amount, {
      currency: data.currency || 'USD',
      plan: data.plan,
      user_id: data.userId,
      ...data.metadata,
    });
  }
}

/**
 * Track a custom metric (e.g., API latency, queue length)
 */
export function trackMetric(data: MetricData) {
  if (!config?.enableCustomMetrics) return;

  SupabaseProvider.recordMetric('custom', data.value, {
    name: data.name,
    unit: data.unit,
    tags: data.tags,
  });
}

function ensureCustomMetricsForServer(appName = 'zeroapi') {
  if (config?.enableCustomMetrics) return;
  const supabaseUrl =
    process.env.NEXT_PUBLIC_SUPABASE_URL || process.env.SUPABASE_URL;
  const supabaseAnonKey =
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseAnonKey) return;

  initMetrics({
    appName: process.env.METRICS_APP_NAME || appName,
    enableSentry: false,
    enablePosthog: false,
    enableCustomMetrics: true,
    enableGa: false,
    supabaseUrl,
    supabaseAnonKey,
  });
}

export function trackAiUsage(data: AiUsageData, options?: { appName?: string }) {
  ensureCustomMetricsForServer(options?.appName ?? 'zeroapi');

  const outcomes = data.outcomes ?? 1;
  const cachedTokensIn = data.cachedTokensIn ?? 0;
  const cacheCreationTokens = data.cacheCreationTokens ?? 0;
  trackMetric({
    name: AI_TOKEN_COST_METRIC,
    value: data.estimatedUsd,
    unit: 'usd',
    tags: {
      feature: data.feature,
      provider: data.provider,
      tokens_in: String(data.tokensIn),
      tokens_out: String(data.tokensOut),
      outcomes: String(outcomes),
      cached_tokens_in: String(cachedTokensIn),
      cache_creation_tokens: String(cacheCreationTokens),
      prompt_cache_hit: cachedTokensIn > 0 ? '1' : '0',
    },
  });

  trackEvent(AI_TOKEN_COST_METRIC, {
    feature: data.feature,
    provider: data.provider,
    tokens_in: data.tokensIn,
    tokens_out: data.tokensOut,
    estimated_usd: data.estimatedUsd,
    outcomes,
    cached_tokens_in: cachedTokensIn,
    cache_creation_tokens: cacheCreationTokens,
    prompt_cache_hit: cachedTokensIn > 0,
  });
}

/**
 * Identify the current user for analytics
 */
export function identifyUser(userId: string, properties?: EventProperties) {
  if (!config) return;

  if (config.enablePosthog) {
    PosthogProvider.identifyUser(userId, properties);
  }

  if (config.enableSentry) {
    SentryProvider.setUser(userId, properties?.email as string);
  }
}

/**
 * Track page views (useful for SPAs)
 */
export function trackPageView(data: PageViewData) {
  if (config?.enablePosthog) {
    PosthogProvider.trackPageView(data);
  }
  GaProvider.trackPageView(data);
}

/** App Hub funnel events — see plans/my-portfolio/FUNNEL_CONTRACT.md */
export function trackHubView(path: string, utmSource?: string | null) {
  trackEvent('buildlogic_hub_view', {
    path,
    utm_source: utmSource ?? undefined,
  });
}

export function trackHubEntryClick(clickSource: string) {
  trackEvent('buildlogic_hub_entry_click', { click_source: clickSource });
}

export function trackHubMatchSubmit(queryLength: number, usedExample: boolean) {
  trackEvent('buildlogic_hub_match_submit', {
    query_length: queryLength,
    used_example: usedExample,
  });
}

export type HubMatchResultProps = {
  method: string;
  confidence: number;
  matchCount: number;
  matchSlugs: string[];
  excludedDown?: string[];
  availabilityChecked?: boolean;
  hasFallback: boolean;
  queryLength: number;
};

export function trackHubMatchResult(props: HubMatchResultProps) {
  const matchSlugs = props.matchSlugs ?? [];
  const excludedDown = props.excludedDown ?? [];
  trackEvent('buildlogic_hub_match_result', {
    match_method: props.method,
    confidence: props.confidence,
    match_count: props.matchCount,
    match_slugs: matchSlugs.join(','),
    excluded_down: excludedDown.join(','),
    excluded_down_count: excludedDown.length,
    availability_checked: props.availabilityChecked ?? false,
    has_fallback: props.hasFallback,
    query_length: props.queryLength,
  });
}

export function trackHubMatchError(errorMessage: string) {
  trackEvent('buildlogic_hub_match_error', { error_message: errorMessage });
}

export function trackHubAppClick(
  appSlug: string,
  rank: number,
  tier: string,
  matchMethod: string,
  clickSource: string,
) {
  trackEvent('buildlogic_hub_app_click', {
    app_slug: appSlug,
    rank,
    tier,
    match_method: matchMethod,
    click_source: clickSource,
  });
}

export function trackHubCatalogClick(appSlug: string, clusterId: string) {
  trackEvent('buildlogic_hub_catalog_click', {
    app_slug: appSlug,
    cluster_id: clusterId,
  });
}

export function trackHubServicesClick(clickSource: string) {
  trackEvent('buildlogic_hub_services_click', { click_source: clickSource });
}

export function trackHubExampleClick(exampleLabel: string) {
  trackEvent('buildlogic_hub_example_click', { example_label: exampleLabel });
}

/**
 * Get app health status from Supabase
 */
export async function getAppHealth() {
  if (!config?.enableCustomMetrics) return null;
  return SupabaseProvider.getAppHealth();
}

/**
 * Get metrics for the current app
 */
export async function getMetrics(
  metricType?: MetricType,
  startDate?: Date,
  endDate?: Date
) {
  if (!config?.enableCustomMetrics) return [];
  return SupabaseProvider.getMetrics(metricType, startDate, endDate);
}

// Re-export GA config so all apps use the same property (ID from env only)
export { getEmpireGaPropertyId, getGaMeasurementId } from './ga-config.js';

export { AI_TOKEN_COST_METRIC, aggregateAiCostRows };

// Re-export types
export type {
  MetricsConfig,
  EventProperties,
  ErrorContext,
  RevenueData,
  MetricData,
  MetricType,
  PageViewData,
  AiUsageData,
  AiCostRow,
  AiCostMetricRecord,
};
